// Các cửa sổ phụ của màn kỳ lương.
import React, { useState } from 'react';
import { PenLine, User, AlertTriangle, FileSpreadsheet, Upload, ScrollText, Factory, Layers3, Check } from 'lucide-react';
import { toast } from 'react-toastify';
import { VARIABLE_SOURCE_LABELS } from '../core/configRules';
import { LEAVE_TYPES, holidayAppliesToEmployee } from '../core/leaveHoliday';
import { splitEmployeeTeams } from '../core/periodInputs';
import { teamWeightsFromLines } from '../core/sourceRegistry';
import { parseInputImportRows } from '../core/periodWorkbench';
import { downloadInputTemplate, readSheetRows } from '../data/exportPayroll';
import { parseSheetNumber } from '../data/sheetFormat';
import { formatCellValue } from './WorkbenchMatrix';
import { Badge, Field, MessageList, Modal, buttonPrimary, buttonSecondary, formatDateTime, formatDay, formatMoney, inputClass } from './ui';

export function ReasonModal({ title, label, confirmLabel, onClose, onSubmit }) {
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    return (
        <Modal title={title} icon={PenLine} onClose={onClose} disabled={busy} size="max-w-lg"
            footer={(
                <>
                    <button className={buttonSecondary} onClick={onClose} disabled={busy}>Hủy</button>
                    <button className={buttonPrimary} disabled={busy || !reason.trim()}
                        onClick={async () => { try { setBusy(true); await onSubmit(reason.trim()); } finally { setBusy(false); } }}>{confirmLabel}</button>
                </>
            )}>
            <Field label={label} required><textarea rows={3} className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
        </Modal>
    );
}

export function ConfirmWarningsModal({ warnings, onClose, onConfirm }) {
    const [checked, setChecked] = useState(false);
    const [busy, setBusy] = useState(false);
    return (
        <Modal title="Xác nhận cảnh báo trước khi gửi duyệt" icon={AlertTriangle} onClose={onClose} disabled={busy}
            footer={(
                <>
                    <button className={buttonSecondary} onClick={onClose} disabled={busy}>Hủy</button>
                    <button className={buttonPrimary} disabled={busy || !checked} onClick={async () => { try { setBusy(true); await onConfirm(); } finally { setBusy(false); } }}>Gửi duyệt</button>
                </>
            )}>
            <MessageList tone="yellow" items={warnings} />
            <label className="flex items-start gap-2 mt-3 text-sm text-gray-800">
                <input type="checkbox" className="mt-1" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
                Tôi đã xem các cảnh báo trên và xác nhận số liệu kỳ lương này đúng. Việc xác nhận được ghi lại cùng kỳ lương.
            </label>
        </Modal>
    );
}

// refData: dữ liệu nền của kỳ (không đặt tên "ref" vì React giữ tên đó cho tham chiếu DOM).
export function EmployeeDetailModal({ row, detail, group, refData: ref, useSnapshot, onClose }) {
    const variableName = (code) => ref.variableByCode.get(code)?.TEN_BIEN || code;
    const agreement = ref.base.agreementByEmployee.get(row.soHd);
    const advances = ref.base.advances.filter((advance) => advance.NHAN_SU_ID === row.soHd);
    const holidays = (ref.base.holidays ?? []).filter((holiday) => holiday.LOAI_NGAY === 'HUONG_LUONG' && holidayAppliesToEmployee(holiday, row.soHd, splitEmployeeTeams(row.to)));
    const leaves = (ref.base.leaveRequests ?? []).filter((request) => request.NHAN_SU_ID === row.soHd);
    const productivity = detail?.CHI_TIET_NANG_SUAT ?? [];
    const teamWeights = teamWeightsFromLines(productivity);
    const formatKg = (value) => Number(value).toLocaleString('vi-VN', { maximumFractionDigits: 3 });
    const traces = row.result?.traces ?? [];
    const valueOf = (code) => row.cells[code]?.value ?? row.result?.values?.[code];
    return (
        <Modal title={`${row.hoTen} — ${row.soHd}`} icon={User} onClose={onClose} size="max-w-5xl">
            <div className="flex flex-wrap gap-2 mb-3 text-xs">
                <Badge tone="blue">Mẫu: {group.templateName}</Badge>
                <Badge>Tổ: {row.to || '-'}</Badge>
                {agreement ? <Badge tone="purple">Thỏa thuận từ {formatDay(agreement.HIEU_LUC_TU)}</Badge> : <Badge tone="red">Chưa có thỏa thuận</Badge>}
                {useSnapshot && <Badge tone="green">Số đã chốt</Badge>}
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
                {[['Tổng cộng', 'TC', 'bg-indigo-50 text-indigo-800'], ['Trừ ứng', 'UNG', 'bg-amber-50 text-amber-800'], ['BH người lao động', 'BH_NLD', 'bg-purple-50 text-purple-800'], ['Thực chi', 'TCHI', 'bg-green-50 text-green-800']].map(([label, code, tone]) => (
                    <div key={code} className={`rounded-lg p-3 ${tone}`}><p className="text-xs">{label}</p><p className="text-lg font-bold">{formatMoney(Math.round(Number(valueOf(code)) || 0)) || 0} đ</p></div>
                ))}
            </div>
            <MessageList tone="yellow" items={row.warnings.map((warning) => warning.message)} />
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mt-3">
                <div className="lg:col-span-3">
                    <h3 className="text-sm font-semibold text-gray-800 mb-1">Cách tính từng khoản</h3>
                    <div className="border border-gray-200 rounded-lg max-h-[55vh] overflow-y-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-gray-50 sticky top-0"><tr><th className="px-2 py-1.5 text-left">Khoản</th><th className="px-2 py-1.5 text-left">Nguồn / công thức</th><th className="px-2 py-1.5 text-right">Giá trị</th></tr></thead>
                            <tbody className="divide-y divide-gray-100">
                                {(useSnapshot ? group.columns.map((column) => ({ code: column.code, source: column.source })) : traces).map((trace) => (
                                    <tr key={trace.code} className={row.cells[trace.code]?.overridden ? 'bg-orange-50' : ''}>
                                        <td className="px-2 py-1.5"><div className="font-mono font-semibold">{trace.code}</div><div className="text-gray-500">{variableName(trace.code)}</div></td>
                                        <td className="px-2 py-1.5">
                                            {trace.formula ? (
                                                <>
                                                    <code className="font-mono text-green-800 break-all">{trace.formula}</code>
                                                    {Object.keys(trace.dependencies ?? {}).length > 0 && (
                                                        <div className="text-gray-500 mt-0.5">{Object.entries(trace.dependencies).map(([code, value]) => `${code} = ${formatCellValue(value, ref.variableByCode.get(code)?.LOAI_DU_LIEU) || 0}`).join('; ')}</div>
                                                    )}
                                                </>
                                            ) : <span className="text-gray-600">{VARIABLE_SOURCE_LABELS[trace.source] || trace.source}</span>}
                                            {row.cells[trace.code]?.overridden && <div className="text-orange-700">Ghi đè: {row.cells[trace.code].overrideReason}</div>}
                                        </td>
                                        <td className="px-2 py-1.5 text-right font-semibold whitespace-nowrap">
                                            {formatCellValue(valueOf(trace.code), ref.variableByCode.get(trace.code)?.LOAI_DU_LIEU) || 0}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="lg:col-span-2 space-y-3">
                    <div>
                        <h3 className="text-sm font-semibold text-gray-800 mb-1">Năng suất trong kỳ</h3>
                        {productivity.length === 0 ? <p className="text-xs text-gray-500">Không có (hoặc chưa nạp năng suất).</p> : (
                            <table className="w-full text-xs border border-gray-200 rounded">
                                <thead className="bg-gray-50"><tr><th className="px-2 py-1 text-left">Công đoạn</th><th className="px-2 py-1 text-right">KL</th><th className="px-2 py-1 text-right">Đơn giá</th><th className="px-2 py-1 text-right">Thành tiền</th></tr></thead>
                                <tbody className="divide-y divide-gray-100">
                                    {productivity.map((line, index) => (
                                        <tr key={index}>
                                            <td className="px-2 py-1">{line.TEN_CONG_DOAN}<div className="text-gray-400">{line.TO ? `Tổ ${line.TO}` : line.TO_PBNS_ID}</div></td>
                                            <td className="px-2 py-1 text-right">{Number(line.KHOI_LUONG).toLocaleString('vi-VN', { maximumFractionDigits: 3 })} {line.DON_VI_TINH}</td>
                                            <td className="px-2 py-1 text-right">{formatMoney(line.DON_GIA)}{line.TIEN_HIEU_CHINH ? <div className="text-gray-400">HC {formatMoney(line.TIEN_HIEU_CHINH)}</div> : null}</td>
                                            <td className="px-2 py-1 text-right font-semibold">{formatMoney(Math.round(line.THANH_TIEN))}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        {teamWeights.length > 0 && (
                            <p className="text-xs text-gray-600 mt-1" title="Mỗi tổ lấy một công đoạn vì các công đoạn cùng tổ tính trên cùng khối lượng; chỉ tính dòng đơn vị kg">
                                Khối lượng tổ (KLNS): {teamWeights.map((item) => `${item.team ? `${item.team} ` : ''}${formatKg(item.weight)} kg`).join(' + ')}
                                {teamWeights.length > 1 && <> = <b>{formatKg(teamWeights.reduce((sum, item) => sum + item.weight, 0))} kg</b></>}
                            </p>
                        )}
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-gray-800 mb-1">Thỏa thuận lương</h3>
                        {agreement ? (
                            <div className="flex flex-wrap gap-1">{Object.entries(agreement.GIA_TRI ?? {}).map(([code, value]) => <Badge key={code} tone="purple">{code}: {formatMoney(value)}</Badge>)}</div>
                        ) : <p className="text-xs text-gray-500">Chưa có thỏa thuận hiệu lực.</p>}
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-gray-800 mb-1">Ngày lễ và nghỉ phép trong kỳ</h3>
                        {holidays.length === 0 && leaves.length === 0 ? <p className="text-xs text-gray-500">Không có (ngày lễ đã xác nhận, đơn nghỉ đã duyệt).</p> : (
                            <ul className="text-xs space-y-0.5">
                                {holidays.map((holiday) => <li key={holiday.ID}>Lễ: <b>{holiday.TEN_NGAY_LE}</b> {formatDay(holiday.TU_NGAY)}{holiday.DEN_NGAY && holiday.DEN_NGAY.getTime() !== holiday.TU_NGAY.getTime() ? ` – ${formatDay(holiday.DEN_NGAY)}` : ''} ({holiday.SO_NGAY_HUONG_LUONG} ngày)</li>)}
                                {leaves.map((request) => <li key={request.MA_DON}>{LEAVE_TYPES[request.LOAI_PHEP]?.label || request.LOAI_PHEP}: {formatDay(request.TU_NGAY)}{request.DEN_NGAY.getTime() !== request.TU_NGAY.getTime() ? ` – ${formatDay(request.DEN_NGAY)}` : ''} (<b>{request.SO_NGAY} ngày</b>{LEAVE_TYPES[request.LOAI_PHEP]?.paid ? '' : ', không lương'})</li>)}
                            </ul>
                        )}
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-gray-800 mb-1">Phiếu ứng</h3>
                        {advances.length === 0 ? <p className="text-xs text-gray-500">Không có.</p> : (
                            <ul className="text-xs space-y-0.5">{advances.map((advance) => <li key={advance.MA_PHIEU}>{formatDay(advance.NGAY_UNG)}: <b>{formatMoney(advance.SO_TIEN)}</b> {advance.GHI_CHU}</li>)}</ul>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
}

export function ImportInputsModal({ period, groups, details, onClose, onApply }) {
    const [result, setResult] = useState(null);
    const inputCodesByTemplate = new Map(groups.map((group) => [group.templateId, group.columns.filter((column) => column.allowInput).map((column) => column.code)]));
    const handleFile = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        try {
            const rows = await readSheetRows(file);
            setResult(parseInputImportRows(rows, { details, inputCodesByTemplate, parseNumber: parseSheetNumber }));
        } catch (error) {
            toast.error('Không đọc được file Excel: ' + error.message);
        }
    };
    const people = result ? Object.keys(result.changes).length : 0;
    return (
        <Modal title="Nhập công từ Excel" icon={FileSpreadsheet} onClose={onClose} size="max-w-3xl"
            footer={(
                <>
                    <button className={buttonSecondary} onClick={onClose}>Đóng</button>
                    <button className={buttonPrimary} disabled={!people} onClick={() => onApply(result.changes)}><Upload className="w-4 h-4" />Đưa {people} người vào bảng</button>
                </>
            )}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div className="bg-gray-50 border rounded-lg p-4 space-y-3">
                    <p className="text-sm font-semibold text-gray-800">1. Tải file mẫu của kỳ</p>
                    <p className="text-xs text-gray-600">Mỗi nhân sự một dòng, điền sẵn số đã nhập. Cột là các biến được nhập của mẫu (ngày công, giờ tăng ca, phụ cấp khác...).</p>
                    <button className={buttonSecondary} onClick={() => downloadInputTemplate(period, groups)}><FileSpreadsheet className="w-4 h-4" />Tải file mẫu</button>
                </div>
                <div className="bg-gray-50 border rounded-lg p-4 space-y-3">
                    <p className="text-sm font-semibold text-gray-800">2. Chọn file đã điền</p>
                    <input type="file" accept=".xlsx,.xls" onChange={handleFile} className="text-sm" />
                    <p className="text-xs text-gray-600">Số được đưa vào bảng để xem lại; bấm Lưu trên bảng mới ghi xuống.</p>
                </div>
            </div>
            {result && (
                <>
                    <MessageList tone="blue" items={[`Đọc được số của ${people} nhân sự.`]} />
                    {result.errors.length > 0 && <div className="mt-2"><MessageList items={result.errors.map((error) => `Dòng ${error.line}: ${error.message}`)} /></div>}
                </>
            )}
        </Modal>
    );
}

/**
 * Đổi mẫu áp dụng của kỳ (như V2) và cập nhật nhân sự theo phạm vi mẫu hiện tại: chọn bộ mẫu, xem trước
 * ai được thêm, bị bỏ, chuyển mẫu, rồi mới ghi. Giữ nguyên bộ mẫu là chỉ cập nhật nhân sự.
 */
export function ChangeTemplatesModal({ period, templates, onPreview, onClose, onApply }) {
    const [selected, setSelected] = useState(period.MAU_IDS ?? []);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const toggle = (code) => { setSelected((prev) => (prev.includes(code) ? prev.filter((item) => item !== code) : [...prev, code])); setPreview(null); };
    const runPreview = async () => {
        try { setBusy(true); setPreview(await onPreview(selected)); } catch (error) { toast.error(error.message); } finally { setBusy(false); }
    };
    const diff = preview?.diff;
    const conflicts = preview?.draft.resolved.conflicts ?? [];
    const total = diff ? diff.added.length + diff.removed.length + diff.changed.length : 0;
    const templatesChanged = JSON.stringify(selected) !== JSON.stringify(period.MAU_IDS ?? []);
    const templateName = (id) => templates.find((template) => template.MA_MAU === id)?.TEN_MAU || id;
    return (
        <Modal title="Đổi mẫu áp dụng" subtitle="Danh sách nhân sự được kiểm tra lại trước khi cập nhật; số đã nhập của người còn trong kỳ được giữ" icon={Layers3} onClose={onClose} disabled={busy} size="max-w-4xl"
            footer={(
                <>
                    <button className={buttonSecondary} onClick={onClose} disabled={busy}>Hủy</button>
                    {preview
                        ? <button className={buttonPrimary} disabled={busy || conflicts.length > 0 || (!total && !templatesChanged)}
                            onClick={async () => { try { setBusy(true); await onApply(selected); } finally { setBusy(false); } }}>Xác nhận thay đổi</button>
                        : <button className={buttonPrimary} disabled={busy || !selected.length} onClick={runPreview}>{busy ? 'Đang kiểm tra...' : 'Xem trước thay đổi'}</button>}
                </>
            )}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                    {templates.map((template) => {
                        const isSelected = selected.includes(template.MA_MAU);
                        return (
                            <button key={template.MA_MAU} type="button" onClick={() => toggle(template.MA_MAU)} disabled={busy}
                                className={`w-full flex items-start gap-2 rounded-lg border px-3 py-2 text-left text-sm ${isSelected ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                                <span className={`mt-0.5 w-4 h-4 flex-shrink-0 flex items-center justify-center rounded border-2 ${isSelected ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300'}`}>{isSelected && <Check className="w-3 h-3 text-white" />}</span>
                                <span><span className="block font-medium text-gray-900">{template.TEN_MAU}</span><span className="block text-xs text-gray-500 font-mono">{template.MA_MAU}</span></span>
                            </button>
                        );
                    })}
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm">
                    {!preview ? (
                        <div className="h-full min-h-[10rem] flex flex-col items-center justify-center text-center text-gray-500">
                            <Layers3 className="w-8 h-8 mb-2" />
                            <p>Chọn bộ mẫu rồi bấm Xem trước để biết ai được thêm, bị bỏ hoặc chuyển mẫu. Giữ nguyên mẫu là cập nhật nhân sự theo phạm vi mẫu hiện tại.</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <div className="grid grid-cols-3 gap-2 text-center">
                                <div className="rounded-lg bg-green-50 p-2"><p className="text-xs text-green-700">Thêm</p><p className="font-semibold">{diff.added.length}</p></div>
                                <div className="rounded-lg bg-red-50 p-2"><p className="text-xs text-red-700">Bỏ</p><p className="font-semibold">{diff.removed.length}</p></div>
                                <div className="rounded-lg bg-yellow-50 p-2"><p className="text-xs text-yellow-800">Chuyển mẫu</p><p className="font-semibold">{diff.changed.length}</p></div>
                            </div>
                            {!total && <p className="text-gray-700">{templatesChanged ? 'Nhân sự không đổi, chỉ cập nhật bộ mẫu của kỳ.' : 'Danh sách nhân sự của kỳ đã khớp với nhân sự và phạm vi mẫu hiện tại.'}</p>}
                            {conflicts.length > 0 && <MessageList items={conflicts.map((conflict) => `${conflict.employeeName} thuộc nhiều mẫu cùng cấp: ${conflict.templates.map((item) => item.templateName).join(', ')}`)} />}
                            {diff.added.length > 0 && <div><p className="font-semibold text-green-700">Thêm {diff.added.length} người</p><p className="text-xs text-gray-700">{diff.added.map((item) => `${item.employeeName} (${item.templateName})`).join(', ')}</p></div>}
                            {diff.changed.length > 0 && <div><p className="font-semibold text-blue-700">Chuyển mẫu {diff.changed.length} người</p><p className="text-xs text-gray-700">{diff.changed.map((item) => `${item.employeeName}: ${templateName(item.previousTemplateId)} → ${item.templateName}`).join(', ')}</p></div>}
                            {diff.removed.length > 0 && (
                                <div>
                                    <p className="font-semibold text-red-700">Bỏ {diff.removed.length} người khỏi kỳ</p>
                                    <p className="text-xs text-gray-700">{diff.removed.map((item) => item.HO_TEN_SNAPSHOT).join(', ')}</p>
                                    <p className="text-xs text-red-700 mt-1">Số đã nhập của những người này sẽ mất.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
}

/** Xem trước lần chốt số: năng suất vừa nạp, số mới của cả kỳ, cảnh báo. Có lỗi chặn thì không cho chốt. */
export function CalculationPreviewModal({ preview, previous, onClose, onConfirm }) {
    const [busy, setBusy] = useState(false);
    const { productivity, summary, warnings } = preview;
    const blocking = warnings.filter((warning) => warning.blocking);
    const nonBlocking = warnings.filter((warning) => !warning.blocking);
    const delta = (current, before) => {
        const diff = (current ?? 0) - (before ?? 0);
        return diff ? <span className={diff > 0 ? 'text-green-700' : 'text-red-700'}> ({diff > 0 ? '+' : ''}{formatMoney(diff)})</span> : null;
    };
    return (
        <Modal title="Xác nhận chốt số" subtitle={productivity ? 'Nạp năng suất, tính lại và ghi kết quả cho kỳ' : 'Tính lại và ghi kết quả cho kỳ'} icon={Factory} onClose={onClose} disabled={busy}
            footer={(
                <>
                    <button className={buttonSecondary} onClick={onClose} disabled={busy}>Để sau</button>
                    <button className={buttonPrimary} disabled={busy || blocking.length > 0} onClick={async () => { try { setBusy(true); await onConfirm(); } finally { setBusy(false); } }}>
                        {busy ? 'Đang chốt số...' : 'Xác nhận chốt số'}
                    </button>
                </>
            )}>
            <div className="space-y-4 text-sm">
                {productivity && (
                    <div className="grid grid-cols-3 gap-3 text-center">
                        <div className="bg-green-50 border border-green-100 rounded-lg p-3"><p className="text-xs text-green-700">Người có năng suất</p><p className="text-xl font-bold text-green-800">{productivity.people}</p></div>
                        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3"><p className="text-xs text-blue-700">Tổng năng suất</p><p className="text-xl font-bold text-blue-800">{formatMoney(productivity.total)}</p></div>
                        <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-3"><p className="text-xs text-yellow-700">Người đổi số năng suất</p><p className="text-xl font-bold text-yellow-800">{productivity.changed}</p></div>
                    </div>
                )}
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
                    <div className="flex justify-between px-4 py-2"><span className="text-gray-600">Nhân sự</span><b>{preview.employeeCount}</b></div>
                    <div className="flex justify-between px-4 py-2"><span className="text-gray-600">Tổng lương</span><span><b>{formatMoney(summary.totalGross)}</b>{delta(summary.totalGross, previous.TONG_LUONG)}</span></div>
                    <div className="flex justify-between px-4 py-2"><span className="text-gray-600">Trừ ứng / BH người lao động</span><span>{formatMoney(summary.totalAdvance)} / {formatMoney(summary.totalInsurance)}</span></div>
                    <div className="flex justify-between px-4 py-2"><span className="text-gray-600">Thực chi</span><span><b>{formatMoney(summary.totalNet)}</b>{delta(summary.totalNet, previous.TONG_THUC_CHI)}</span></div>
                </div>
                <MessageList items={blocking.map((warning) => warning.message)} />
                <MessageList tone="yellow" items={[
                    ...nonBlocking.map((warning) => warning.message),
                    ...(productivity?.unmatchedCount ? [`Sẽ bỏ qua ${productivity.unmatchedCount} dòng năng suất (${formatMoney(productivity.unmatchedAmount)} đ) chưa gắn Số HĐ phụ trách. Gắn ở Phân bổ nhân sự → Ghép Số HĐ.`] : []),
                    ...(productivity?.outside.length ? [`${productivity.outside.length} người có năng suất nhưng không thuộc kỳ này (${productivity.outside.map((item) => `${item.soHd}: ${formatMoney(item.amount)}`).join(', ')}).`] : []),
                    ...(productivity?.mergedCount ? [`${productivity.mergedCount} dòng năng suất gộp nhiều phân bổ khác người phụ trách vào cùng tổ–công đoạn; tiền tính cho người của phân bổ đầu tiên.`] : []),
                ]} />
            </div>
        </Modal>
    );
}

export function AuditModal({ rows, onClose }) {
    return (
        <Modal title="Nhật ký kỳ lương" icon={ScrollText} onClose={onClose} size="max-w-3xl">
            {rows.length === 0 ? <p className="text-sm text-gray-500">Chưa có nhật ký.</p> : (
                <table className="w-full text-xs">
                    <thead className="bg-gray-50"><tr><th className="px-2 py-1.5 text-left">Thời gian</th><th className="px-2 py-1.5 text-left">Người</th><th className="px-2 py-1.5 text-left">Thao tác</th><th className="px-2 py-1.5 text-left">Chi tiết</th></tr></thead>
                    <tbody className="divide-y divide-gray-100">
                        {rows.map((row) => (
                            <tr key={row.ID}><td className="px-2 py-1.5 whitespace-nowrap">{formatDateTime(row.THOI_GIAN)}</td><td className="px-2 py-1.5">{row.NGUOI}</td><td className="px-2 py-1.5 font-medium">{row.HANH_DONG}</td><td className="px-2 py-1.5 text-gray-600 break-all">{row.CHI_TIET}</td></tr>
                        ))}
                    </tbody>
                </table>
            )}
        </Modal>
    );
}
